import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Paper, Container, Button, TextField, MenuItem,
  Stepper, Step, StepLabel, IconButton, InputAdornment, Divider,
  FormControl, FormLabel, FormControlLabel, Radio, RadioGroup,
  Chip, Tooltip, useTheme, alpha, Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  HelpOutline as HelpIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { sectors } from '../data/marketplaceData';
import * as marketplaceApi from '../services/marketplace';
import { useAuth } from '../context/AuthContext';
import { uploadMultipleDocuments } from '../services/upload';

// Custom styled components
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(3),
  fontWeight: 600,
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: 40,
    height: 3,
    backgroundColor: theme.palette.secondary.main,
  }
}));

const StepContent = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
}));

const ChipContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const FilePreview = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: theme.shape.borderRadius,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: theme.palette.divider,
}));

// Main component
const SubmitInvestorRequestForm = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Form state
  const [formData, setFormData] = useState({
    // Basic Info
    projectName: '',
    sector: '',
    preferredIndustry: '',
    preferredLocation: '',
    investmentType: 'Equity',
    
    // Investor Info
    organization: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    
    // Project Details
    projectGoal: '',
    investmentRange: '',
    requiredROI: '',
    timeline: '',
    preferredPartnerType: [] as string[],
    additionalCriteria: '',
    
    // Supporting Materials
    links: [''],
    documents: [] as File[],
  });

  // Handle input change
  const handleChange = (event: { target: { name: any; value: any; }; }) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle chip input for preferred partner types
  const handleAddPartnerType = (type: string) => {
    if (!formData.preferredPartnerType.includes(type)) {
      setFormData({
        ...formData,
        preferredPartnerType: [...formData.preferredPartnerType, type],
      });
    }
  };

  const handleRemovePartnerType = (type: string) => {
    setFormData({
      ...formData,
      preferredPartnerType: formData.preferredPartnerType.filter(item => item !== type),
    });
  };

  // Handle file uploads
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    setFormData({
      ...formData,
      documents: [...formData.documents, ...files],
    });
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = [...formData.documents];
    updatedFiles.splice(index, 1);
    setFormData({
      ...formData,
      documents: updatedFiles,
    });
  };

  // Handle links array
  const handleLinkChange = (index: number, value: string) => {
    const updatedLinks = [...formData.links];
    updatedLinks[index] = value;
    setFormData({
      ...formData,
      links: updatedLinks,
    });
  };

  const handleAddLink = () => {
    setFormData({
      ...formData,
      links: [...formData.links, ''],
    });
  };

  const handleRemoveLink = (index: number) => {
    const updatedLinks = [...formData.links];
    updatedLinks.splice(index, 1);
    setFormData({
      ...formData,
      links: updatedLinks,
    });
  };

  // Step navigation
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    window.scrollTo(0, 0);
  };

  // Form submission
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Upload documents to Cloudinary if any
      let uploadedDocuments: Array<{ url: string; name: string }> = [];
      if (formData.documents.length > 0) {
        try {
          const uploadResult = await uploadMultipleDocuments(formData.documents);
          uploadedDocuments = uploadResult.files.map(file => ({
            url: file.url,
            name: file.originalName
          }));
        } catch (error) {
          console.error('Error uploading documents:', error);
          setIsSubmitting(false);
          alert('Failed to upload documents. Please try again.');
          return;
        }
      }

      // Prepare data for API
      const requestData = {
        projectName: formData.projectName,
        sector: formData.sector,
        investor: formData.contactName,
        organization: formData.organization,
        goal: formData.projectGoal,
        preferredIndustry: formData.preferredIndustry || undefined,
        location: formData.preferredLocation || undefined,
        investmentType: formData.investmentType || undefined,
        proposal: formData.additionalCriteria || undefined,
        roi: formData.requiredROI || undefined,
        links: formData.links.filter((l: string) => l.trim()).map((url: string) => ({ url: url.trim(), title: url.trim() })),
        documents: uploadedDocuments,
        nextSteps: formData.timeline || undefined
      };

      await marketplaceApi.createInvestorRequest(requestData);

      setIsSubmitting(false);
      setIsSubmitted(true);
      // Navigate to success view (next step)
      setActiveStep(4);
      window.scrollTo(0, 0);

      // Navigate to marketplace after brief delay
      setTimeout(() => {
        navigate('/marketplace');
      }, 3000);
    } catch (error) {
      console.error('Error submitting investor request:', error);
      setIsSubmitting(false);
      setErrors({ submit: 'Failed to submit investor request. Please try again.' });
    }
  };

  // Partner type options
  const partnerTypeOptions = [
    'Startup', 'Established Company', 'Research Institution', 'University', 
    'Public Entity', 'Private Entity', 'Non-Profit', 'Individual Entrepreneur'
  ];

  // Steps content definition
  const steps = [
    'Basic Information',
    'Investor Details',
    'Project Criteria',
    'Supporting Materials',
  ];

  // Validation for each step
  const isStepValid = (step: number) => {
    switch (step) {
      case 0:
        return formData.projectName && formData.sector;
      case 1:
        return formData.organization && formData.contactName && formData.email;
      case 2:
        return formData.projectGoal && formData.investmentRange;
      case 3:
        return true; // Supporting materials are optional
      default:
        return false;
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <StepContent>
            <SectionTitle variant="h6">What kind of project are you looking for?</SectionTitle>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  name="projectName"
                  label="Project Name"
                  value={formData.projectName}
                  onChange={handleChange}
                  placeholder="e.g., Advanced Manufacturing Robotics"
                  helperText="Provide a clear, descriptive name for the type of project you're seeking"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DescriptionIcon color="secondary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  select
                  name="sector"
                  label="Primary Sector"
                  value={formData.sector}
                  onChange={handleChange}
                  helperText="Select the main industry sector you're interested in"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon color="secondary" />
                      </InputAdornment>
                    ),
                  }}
                >
                  {sectors.filter(sector => sector !== "All Sectors").map((sector) => (
                    <MenuItem key={sector} value={sector}>
                      {sector}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="preferredIndustry"
                  label="Preferred Industry Specifics"
                  value={formData.preferredIndustry}
                  onChange={handleChange}
                  placeholder="e.g., Manufacturing Automation - Assembly Line Robotics"
                  helperText="Provide more specific details about your industry focus"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="preferredLocation"
                  label="Preferred Project Location"
                  value={formData.preferredLocation}
                  onChange={handleChange}
                  placeholder="e.g., Riyadh or Jazan Economic City"
                  helperText="Where would you prefer the project to be located?"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationIcon color="secondary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Investment Type</FormLabel>
                  <RadioGroup
                    row
                    name="investmentType"
                    value={formData.investmentType}
                    onChange={handleChange}
                  >
                    <FormControlLabel value="Equity" control={<Radio />} label="Equity" />
                    <FormControlLabel value="Debt" control={<Radio />} label="Debt" />
                    <FormControlLabel value="Mixed" control={<Radio />} label="Mixed" />
                    <FormControlLabel value="Partnership" control={<Radio />} label="Partnership" />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </StepContent>
        );
      
      case 1:
        return (
          <StepContent>
            <SectionTitle variant="h6">Tell us about your organization</SectionTitle>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  name="organization"
                  label="Organization Name"
                  value={formData.organization}
                  onChange={handleChange}
                  placeholder="e.g., Saudi Industrial Ventures"
                  helperText="The name of your company or investment entity"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon color="secondary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  name="contactName"
                  label="Contact Person"
                  value={formData.contactName}
                  onChange={handleChange}
                  placeholder="e.g., Mohammed Al-Harbi"
                  helperText="Primary point of contact for this request"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  name="email"
                  label="Contact Email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="e.g., contact@saudiventures.sa"
                  helperText="Email address for interested parties to contact you"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="phone"
                  label="Contact Phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="e.g., +966 12 345 6789"
                  helperText="Phone number (optional)"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="website"
                  label="Organization Website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="e.g., https://saudiventures.sa"
                  helperText="Your organization's website (optional)"
                />
              </Grid>
            </Grid>
          </StepContent>
        );
      
      case 2:
        return (
          <StepContent>
            <SectionTitle variant="h6">Define your investment criteria</SectionTitle>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={4}
                  name="projectGoal"
                  label="Project Goal / Description"
                  value={formData.projectGoal}
                  onChange={handleChange}
                  placeholder="e.g., Seeking robotics and automation technologies to enhance manufacturing productivity in Saudi industrial cities."
                  helperText="Describe what you're looking for and your investment goals"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  name="investmentRange"
                  label="Investment Range"
                  value={formData.investmentRange}
                  onChange={handleChange}
                  placeholder="e.g., $5M - $20M"
                  helperText="What is your target investment amount range?"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MoneyIcon color="secondary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="requiredROI"
                  label="Target ROI / Return Expectations"
                  value={formData.requiredROI}
                  onChange={handleChange}
                  placeholder="e.g., 20% IRR over 5 years"
                  helperText="What returns are you targeting for this investment?"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="timeline"
                  label="Investment Timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  placeholder="e.g., Seeking to invest within 6 months"
                  helperText="When are you looking to make this investment?"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Preferred Partner Types
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Select the types of partners you're interested in working with
                </Typography>
                
                <ChipContainer>
                  {partnerTypeOptions.map((type) => (
                    <Chip
                      key={type}
                      label={type}
                      onClick={() => handleAddPartnerType(type)}
                      color={formData.preferredPartnerType.includes(type) ? "secondary" : "default"}
                      variant={formData.preferredPartnerType.includes(type) ? "filled" : "outlined"}
                      clickable
                      onDelete={formData.preferredPartnerType.includes(type) ? 
                        () => handleRemovePartnerType(type) : undefined}
                      deleteIcon={formData.preferredPartnerType.includes(type) ? <DeleteIcon /> : undefined}
                    />
                  ))}
                </ChipContainer>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  name="additionalCriteria"
                  label="Additional Criteria or Requirements"
                  value={formData.additionalCriteria}
                  onChange={handleChange}
                  placeholder="e.g., Looking for technologies with existing IP protection and proven market validation."
                  helperText="Any other specific requirements or preferences"
                />
              </Grid>
            </Grid>
          </StepContent>
        );
      
      case 3:
        return (
          <StepContent>
            <SectionTitle variant="h6">Add supporting materials</SectionTitle>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  External Links
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Add links to relevant resources, your company website, or investment criteria
                </Typography>
                
                {formData.links.map((link, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                      fullWidth
                      name={`link-${index}`}
                      label={`Link ${index + 1}`}
                      value={link}
                      onChange={(e) => handleLinkChange(index, e.target.value)}
                      placeholder="e.g., https://yourcompany.com/investor-info"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            https://
                          </InputAdornment>
                        ),
                      }}
                    />
                    <IconButton 
                      color="error" 
                      onClick={() => handleRemoveLink(index)}
                      disabled={formData.links.length === 1 && index === 0}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddLink}
                  sx={{ mt: 1 }}
                >
                  Add Another Link
                </Button>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Supporting Documents
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Upload documents like investment criteria, company profile, or sector research
                </Typography>
                
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  sx={{ mt: 1 }}
                >
                  Upload Document
                  <VisuallyHiddenInput 
                    type="file" 
                    multiple 
                    onChange={handleFileUpload}
                  />
                </Button>
                
                <Box sx={{ mt: 2 }}>
                  {formData.documents.length > 0 ? (
                    formData.documents.map((file, index) => (
                      <FilePreview key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <DescriptionIcon sx={{ mr: 1 }} />
                          <Typography variant="body2">
                            {file.name}
                          </Typography>
                        </Box>
                        <IconButton color="error" onClick={() => handleRemoveFile(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </FilePreview>
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                      No documents uploaded yet
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </StepContent>
        );
      
      case 4: // Success step
        return (
          <StepContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <Box
                  component="svg"
                  sx={{ width: 40, height: 40, fill: 'none', stroke: theme.palette.success.main, strokeWidth: 2 }}
                  viewBox="0 0 24 24"
                >
                  <path d="M20 6L9 17l-5-5" />
                </Box>
              </Box>
              
              <Typography variant="h4" gutterBottom>
                Request Submitted Successfully!
              </Typography>
              
              <Typography variant="body1" paragraph>
                Thank you for submitting your investor request. Your request has been received and will be reviewed by our team.
              </Typography>
              
              <Typography variant="body1" paragraph>
                You will receive a confirmation email shortly. We'll contact you once your request is published on the marketplace.
              </Typography>
              
              <Box sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  onClick={() => navigate('/marketplace')}
                  sx={{ mr: 2 }}
                >
                  Return to Marketplace
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="large"
                  onClick={() => {
                    setActiveStep(0);
                    setFormData({
                      projectName: '',
                      sector: '',
                      preferredIndustry: '',
                      preferredLocation: '',
                      investmentType: 'Equity',
                      organization: '',
                      contactName: '',
                      email: '',
                      phone: '',
                      website: '',
                      projectGoal: '',
                      investmentRange: '',
                      requiredROI: '',
                      timeline: '',
                      preferredPartnerType: [],
                      additionalCriteria: '',
                      links: [''],
                      documents: [],
                    });
                    setIsSubmitted(false);
                  }}
                >
                  Submit Another Request
                </Button>
              </Box>
            </Box>
          </StepContent>
        );
      
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      {/* Header section */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
        <IconButton 
          onClick={() => navigate('/marketplace')}
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" fontWeight={600}>
          Submit Investor Request
        </Typography>
      </Box>
      
      {/* Introduction */}
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.9)}, ${alpha(theme.palette.secondary.dark, 0.8)})`,
          color: 'white',
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Looking for specific investment opportunities?
            </Typography>
            <Typography variant="body1" paragraph>
              Fill out this form to describe what you're looking for. Project owners will be able to see your request and connect with you if they have matching opportunities.
            </Typography>
            <Typography variant="body1">
              Our team will review your request before publishing it to ensure quality and relevance.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' }, alignItems: 'center' }}>
            <Box
              component="img"
              src="/api/placeholder/240/240"
              alt="Investor Request"
              sx={{
                maxWidth: '80%',
                borderRadius: '50%',
                border: '4px solid rgba(255,255,255,0.2)',
              }}
            />
          </Grid>
        </Grid>
      </Paper>
      
      {/* Stepper */}
      <Stepper 
        activeStep={activeStep} 
        alternativeLabel
        sx={{ mb: 4 }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      
      {/* Form content */}
      {renderStepContent(activeStep)}
      
      {/* Navigation buttons */}
      {activeStep < 4 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            onClick={activeStep === 0 ? () => navigate('/marketplace') : handleBack}
            startIcon={activeStep === 0 ? <ArrowBackIcon /> : undefined}
          >
            {activeStep === 0 ? 'Return to Marketplace' : 'Back'}
          </Button>
          <Box>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSubmit}
                disabled={!isStepValid(activeStep) || isSubmitting}
                sx={{ minWidth: 120 }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleNext}
                disabled={!isStepValid(activeStep)}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      )}
      
      {/* Help Info */}
      {activeStep < 4 && (
        <Alert 
          severity="info" 
          icon={<HelpIcon />}
          sx={{ mt: 4, borderRadius: 2 }}
        >
          <Typography variant="subtitle2">
            Need help with your submission?
          </Typography>
          <Typography variant="body2">
            Contact our support team at support@value-chain.global or call +966 12 345 6789 for assistance.
          </Typography>
        </Alert>
      )}
    </Container>
  );
};

export default SubmitInvestorRequestForm;